import React, { useEffect, useState, useCallback, useContext } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { getMyAccountApi } from "../../utils/api/Account/getMyAccountApi";
import { updateMyAccountApi } from "../../utils/api/Account/updateMyAccountApi";
import { LoadingCircle } from "../../components/UI/Custom/LoadingCircle";
import { InputSelector } from "../../components/UI/InputSelector";
import { handleError } from "../../utils/custom/errorHandler";
import { Notification } from "../../components/UI/Notifications";
import { permissionTranslations, getPermissionLabel, getPermissionDescription } from "../../utils/translations/permissionTranslations";
import { LangContext } from "../../context/LangContext";
import { ThemeContext } from "../../context/ThemeContext"; 
import styles from "../../styles/pages/Account/AccountPage.module.css";
import { Title } from "../../components/UI/Custom/Title";

const translations = {
    ru: {
        title: "Мой аккаунт",
        fieldLabels: {
            id: "ID",
            first_name: "Имя",
            last_name: "Фамилия",
            email: "Email",
            role_id: "ID роли",
            role_name: "Название роли",
        },
        noData: "Н/Д",
        editActionTitle: "Редактировать",
        copyActionTitle: "Копировать",
        save: "Сохранить",
        cancel: "Отмена",
        editAccount: "Редактировать аккаунт",
        permissions: "Разрешения",
        noPermissions: "Разрешения не найдены.",
        loading: "Загрузка данных аккаунта...",
        loadingError: "Ошибка загрузки данных аккаунта. Пожалуйста, попробуйте снова.",
        noAccountData: "Данные аккаунта администратора не найдены.",
        permissionCopyTitle: "Копировать название разрешения",
        permissionName: (name) => `Название разрешения: ${name}`,
    },
    en: {
        title: "My Account",
        fieldLabels: {
            id: "ID",
            first_name: "First Name",
            last_name: "Last Name",
            email: "Email",
            role_id: "Role ID",
            role_name: "Role Name",
        },
        noData: "N/A",
        editActionTitle: "Edit",
        copyActionTitle: "Copy",
        save: "Save",
        cancel: "Cancel",
        editAccount: "Edit Account",
        permissions: "Permissions",
        noPermissions: "Permissions not found.",
        loading: "Loading account data...",
        loadingError: "Error loading account data. Please try again.",
        noAccountData: "Admin account data not found.",
        permissionCopyTitle: "Copy permission name",
        permissionName: (name) => `Permission name: ${name}`,
    },
};

export const AccountPage = () => {
    const [adminInfo, setAdminInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editableAdminInfo, setEditableAdminInfo] = useState(null);
    const [focusedField, setFocusedField] = useState(null);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [permissionStates, setPermissionStates] = useState({});
    const [groupedPermissions, setGroupedPermissions] = useState({});

    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext); 
    const t = translations[langSelected] || translations.ru;
    const pT = permissionTranslations;

    const SUFFIXES_FOR_DROPDOWN = ['.all', '.desk', '.assigned'];

    const accountFields = [
        { key: "id", label: t.fieldLabels.id, editable: false },
        { key: "first_name", label: t.fieldLabels.first_name, editable: true },
        { key: "last_name", label: t.fieldLabels.last_name, editable: true },
        { key: "email", label: t.fieldLabels.email, editable: true },
        { key: "role.id", label: t.fieldLabels.role_id, editable: false },
        { key: "role.name", label: t.fieldLabels.role_name, editable: false },
    ];

    const getAccountValueByKey = (data, key) => {
        return key.split(".").reduce((o, i) => (o ? o[i] : null), data);
    };

    const loadAdminData = useCallback(async () => {
        setIsLoading(true);
        setErrors({});
        setTouched({});

        try {
            const data = await getMyAccountApi();
            setAdminInfo(data);
            setEditableAdminInfo(data);

            const permissionsByGroup = data.permissions.reduce((acc, permission) => {
                const groupName = permission.group || "Без группы";
                if (!acc[groupName]) {
                    acc[groupName] = [];
                }
                acc[groupName].push(permission);
                return acc;
            }, {});
            setGroupedPermissions(permissionsByGroup);

            const initialPermissionStates = data.permissions.reduce((acc, permission) => {
                acc[permission.name] = true;
                return acc;
            }, {});
            setPermissionStates(initialPermissionStates);
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAdminData();
    }, [loadAdminData]);

    const handleCopyable = (label, value) => {
        if (typeof navigator === "undefined" || !value) return;

        navigator.clipboard?.writeText(String(value));
    };

    const handleFieldChange = useCallback((name, value) => {
        setEditableAdminInfo((prev) => ({ ...prev, [name]: value }));
        setTouched((prev) => ({ ...prev, [name]: true }));
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[name]) {
                delete newErrors[name];
            }
            if (newErrors.general) {
                delete newErrors.general;
            }
            return newErrors;
        });
    }, []);

    const handleFocus = useCallback((name) => {
        setFocusedField(name);
        setTouched((prev) => ({ ...prev, [name]: true }));
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[name]) {
                delete newErrors[name];
            }
            if (newErrors.general) {
                delete newErrors.general;
            }
            return newErrors;
        });
    }, []);

    const handleBlur = useCallback((name) => {
        setFocusedField(null);
        setTouched((prev) => ({ ...prev, [name]: true }));
    }, []);

    const handleSaveClick = async () => {
        setErrors({});
        setIsLoading(true);

        try {
            const dataToUpdate = {
                first_name: editableAdminInfo.first_name,
                last_name: editableAdminInfo.last_name,
                email: editableAdminInfo.email,
            };

            await updateMyAccountApi(dataToUpdate);
            await loadAdminData();
            setIsEditing(false);
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            const newTouched = {};
            accountFields.forEach(field => {
                if (field.editable) {
                    newTouched[field.key] = true;
                }
            });
            setTouched(prev => ({ ...prev, ...newTouched }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditableAdminInfo(adminInfo);
        setErrors({});
        setTouched({});
        if (adminInfo && adminInfo.permissions) {
            const initialPermissionStates = adminInfo.permissions.reduce((acc, permission) => {
                acc[permission.name] = true;
                return acc;
            }, {});
            setPermissionStates(initialPermissionStates);
        }
    };

    const renderField = (fieldConfig) => {
        const { key, label, editable } = fieldConfig;
        const value = getAccountValueByKey(adminInfo, key);
        const editableValue = getAccountValueByKey(editableAdminInfo, key);
        const hasError = !!errors[key] && !!touched[key];
        const errorMessage = errors[key];

        return (
            <div className={styles.infoItem} key={key}>
                <div className={styles.labelBox}>{label}:</div>
                <div className={styles.displayBox}>
                    {isEditing && editable ? (
                        <InputSelector
                            filterName={key}
                            selectedValue={editableValue}
                            handleFilterChange={handleFieldChange}
                            hasError={hasError}
                            errorMessage={errorMessage}
                            isFocused={focusedField === key}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            type={key === "email" ? "email" : "text"}
                        />
                    ) : (
                        <span className={styles.displayValue}>{value || t.noData}</span>
                    )}
                </div>
                <div className={styles.actionBox}>
                    {editable && !isEditing && (
                        <div
                            className={`${styles.icon} ${styles.editIcon}`}
                            onClick={() => setIsEditing(true)}
                            title={t.editActionTitle}
                        >
                            <EditIcon />
                        </div>
                    )}
                    <div
                        className={`${styles.icon} ${styles.copyIcon}`}
                        onClick={() => handleCopyable(label, value)}
                        title={t.copyActionTitle}
                    >
                        <ContentCopyIcon />
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return <LoadingCircle size={30} thickness={1} />;
    }

    if (!adminInfo && Object.keys(errors).length > 0) {
        return (
            <div className={`${styles.error} ${styles.fullPageError} ${theme === 'dark' ? styles.darkError : ''}`}>
                {errors.general || t.loadingError}
            </div>
        );
    }

    if (!adminInfo) {
        return (
            <div className={`${styles.noData} ${theme === 'dark' ? styles.darkNoData : ''}`}>
                {t.noAccountData}
            </div>
        );
    }

    return (

        <div className={`${styles.accountPage} ${theme === 'dark' ? styles.darkPage : ''}`}>
            <Title h2={t.title} activeSpan={false} />
            {errors.general && <Notification message={errors.general} type={"error"} />}

            <div className={`${styles.accountContainer} ${theme === 'dark' ? styles.darkContainer : ''}`}>
                <div className={styles.infoGrid}>
                    {accountFields.map((field) => renderField(field))}
                </div>

                <div className={styles.editActions}>
                    {isEditing ? (
                        <>
                            <button className={styles.saveButton} onClick={handleSaveClick} disabled={isLoading}>
                                <CheckIcon /> {t.save}
                            </button>
                            <button className={styles.cancelButton} onClick={handleCancelEdit} disabled={isLoading}>
                                <CloseIcon /> {t.cancel}
                            </button>
                        </>
                    ) : (
                        <button
                            className={styles.editButton}
                            onClick={() => setIsEditing(true)}
                            disabled={isLoading}
                        >
                            <EditIcon /> {t.editAccount}
                        </button>
                    )}
                </div>

                <div className={styles.permissionsSection}>
                    <h3 className={styles.subtitle}>{t.permissions}</h3>
                    {Object.keys(groupedPermissions).length > 0 ? (
                        Object.keys(groupedPermissions).map((groupName) => (
                            <div key={groupName} className={`${styles.permissionGroup} ${theme === 'dark' ? styles.darkPermissionGroup : ''}`}>
                                <h4 className={styles.permissionGroupTitle}>
                                    {pT.groups[groupName]?.[langSelected] || groupName}
                                </h4>
                                <ul className={styles.permissionList}>
                                    {groupedPermissions[groupName].map((permission, index) => {
                                        let translatedName = null;
                                        const matchingSuffix = SUFFIXES_FOR_DROPDOWN.find(suffix =>
                                            permission.name.endsWith(suffix)
                                        );
                                        if (matchingSuffix) {
                                            const baseName = permission.name.substring(0, permission.name.length - matchingSuffix.length);
                                            translatedName = pT.names[baseName]?.[langSelected] || null;
                                        }
                                        return (
                                            <li key={index} className={`${styles.permissionItem} ${theme === 'dark' ? styles.darkPermissionItem : ''}`}>
                                                <div className={styles.permissionDetail}>
                                                    <span className={styles.permissionDisplayName}>
                                                        {getPermissionLabel(permission, langSelected)}
                                                        {translatedName && (
                                                            <span className={styles.translatedLabel}>
                                                                ({translatedName})
                                                            </span>
                                                        )}
                                                    </span>
                                                    <label className={styles.switch}>
                                                        <input
                                                            type="checkbox"
                                                            checked={permissionStates[permission.name] || false}
                                                            readOnly
                                                        />
                                                        <span className={`${styles.slider} ${styles.round}`}></span>
                                                    </label>
                                                </div>
                                                <div className={styles.permissionDescription}>
                                                    <p> {getPermissionDescription(permission, langSelected)}</p>
                                                    <ContentCopyIcon
                                                        className={`${styles.icon} ${styles.smallIcon}`}
                                                        onClick={() =>
                                                            handleCopyable(
                                                                t.permissionName(permission.name),
                                                                permission.name
                                                            )
                                                        }
                                                        title={t.permissionCopyTitle}
                                                    />
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))
                    ) : (
                        <p className={`${styles.noPermissions} ${theme === 'dark' ? styles.darkNoPermissions : ''}`}>{t.noPermissions}</p>
                    )}
                </div>
            </div>
        </div>
    );
};
